import { useCallback, useMemo } from 'react';

import { isGroupHeader } from '../helpers';
import {
  GenericItem,
  GroupHeader,
  InputItemGroup,
  FlatGroupedItem,
} from '../types';

const getStartingIndices = <TItem extends GenericItem>(
  groupedItems: InputItemGroup<TItem>[]
) => {
  return groupedItems.reduce((indices, _, index) => {
    if (index === 0) {
      // the first group will always map to the first item
      return indices.set(0, 0);
    }
    return indices.set(
      index,
      groupedItems
        .slice(0, index)
        .reduce((acc, group) => acc + group.items.length, 0)
    );
  }, new Map<number, number>());
};

/**
 * Strips the grouping out of dropdown items, transforming them into standard dropdown items, including a dummy item for the group header
 * @param groupedItems The grouped dropdown items
 * @returns flattened items without any context of grouping
 */
const flattenGroupItems = <TItem extends GenericItem>(
  groupedItems: InputItemGroup<TItem>[],
  startingIndices: Map<number, number>
): (GroupHeader | FlatGroupedItem<TItem>)[] =>
  groupedItems.reduce<(GroupHeader | FlatGroupedItem<TItem>)[]>(
    (prev, curr, groupIndex) => {
      const groupKey = `group${groupIndex}`;
      const groupHeader: GroupHeader = {
        label: curr.label,
        groupKey,
        isGroupHeader: true,
      };

      const itemsWithGroupKey = curr.items.map((item, itemIndex) => ({
        ...item,
        index: (startingIndices.get(groupIndex) || 0) + itemIndex,
        groupKey,
      }));

      return [...prev, groupHeader, ...itemsWithGroupKey];
    },
    []
  );

type UseGroupedItemsProps<TItem extends GenericItem> = {
  groupedItems: InputItemGroup<TItem>[];
};

/**
 * A hook used to manage all logic needed for grouped items (menu sections/headings).
 * When using this hook, its return values should be used over any duplicates from other hooks
 */
export default <TItem extends GenericItem>({
  groupedItems,
}: UseGroupedItemsProps<TItem>) => {
  const startingIndices = useMemo(
    () => getStartingIndices(groupedItems),
    [groupedItems]
  );

  const flatItemsWithGroupHeaders = useMemo(
    () => flattenGroupItems(groupedItems, startingIndices),
    [groupedItems, startingIndices]
  );

  const getMenuItems = useCallback(
    (
      filteredItems: FlatGroupedItem<TItem>[]
    ): (GroupHeader | FlatGroupedItem<TItem>)[] => {
      const filteredMenuItems: (GroupHeader | FlatGroupedItem<TItem>)[] = [];

      filteredItems.forEach((filteredItem, i) => {
        const groupHeader = flatItemsWithGroupHeaders.find(
          (item) =>
            item.isGroupHeader && item.groupKey === filteredItem.groupKey
        );

        if (groupHeader && !filteredMenuItems.includes(groupHeader)) {
          filteredMenuItems.push(groupHeader);
        }

        filteredItem.index = i;
        filteredMenuItems.push(filteredItem);
      });

      return filteredMenuItems;
    },
    [flatItemsWithGroupHeaders]
  );

  const flatItems = useMemo(
    () => flatItemsWithGroupHeaders.filter((item) => !isGroupHeader(item)),
    [flatItemsWithGroupHeaders]
  );

  return {
    items: flatItems as TItem[],
    isGroupHeader,
    getMenuItems,
  };
};
